/**
 * The console's list — ONE THREAD PER COUNTERPARTY, and AT MOST TWO ROWS IN IT
 * (`docs/conversation-model.md` §6.3).
 *
 * `GET /api/v1/threads` answers every conversation the viewer is in, grouped by
 * the other person's immutable X id. There is exactly one knock per sender and
 * door for as long as both people exist (§2), so a thread holds one row per
 * DIRECTION and never a list: `incoming` is the knock at the reader's own door,
 * `outgoing` is the knock they made at the other person's. Either may be
 * absent; both absent is not a conversation.
 *
 * The chapters are gone with the mechanism that made them. A knock no longer
 * ends and no longer gets replaced by a follow-up, so there is nothing to
 * order and nothing to divide: one row, one transcript, per direction.
 *
 * The server invents nothing: an incoming row is exactly the recipient's own
 * read of that knock and an outgoing row is exactly the sender's own list row,
 * so this file parses each with the parser that surface already has
 * (`normaliseRecipientKnock`, `normaliseMyKnock`) and reads only the thread's
 * own facts on top — who, and whether the door is open both ways.
 *
 * The thread id is the server's and is OPAQUE here: the X id it is built from
 * is a key, never a field, and nothing in the web needs to take it apart.
 */
import { call, get } from "./core";
import { normaliseRecipientKnock, type RecipientKnock } from "./inbox";
import {
  normaliseMyKnock,
  ownOriginAvatar,
  type KnockReadFailureCode,
  type MyKnock,
} from "./knock";

export type Thread = {
  id: string;
  counterparty: {
    /** Bare, no `@`. Empty when the person has no handle to show. */
    handle: string;
    display_name: string;
    /** A path on OUR origin, or null — the same gate every picture passes. */
    avatar: string | null;
  };
  /** The door is open both ways (§9): both directions `let_in`. */
  connected: boolean;
  /**
   * WHEN THIS CONVERSATION LAST MOVED, as the SERVER derives it (§6.3) — and
   * the derivation is the point, which is why it is read rather than computed.
   * A declined row's activity moves only when the doorman's read moves: a
   * sender arguing with the gatekeeper after a decline does not resurface in
   * the owner's list on every line. Nothing on this side can know that; the row
   * timestamps it would have to add up say only when each row opened.
   *
   * `null` from a server that sends none, and then the row falls back to the
   * side it wears, exactly as it did before this field existed.
   */
  last_activity_at: string | null;
  /** The knock standing at the reader's own door, if there is one. */
  incoming: RecipientKnock | null;
  /** The knock the reader made at the other person's door, if there is one. */
  outgoing: MyKnock | null;
};

export type ThreadsResult =
  { ok: true; threads: Thread[] } | { ok: false; code: KnockReadFailureCode };

function normaliseThread(raw: unknown): Thread | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  const id = typeof t["id"] === "string" ? t["id"] : "";
  if (!id) return null;
  const who = (
    t["counterparty"] && typeof t["counterparty"] === "object" ? t["counterparty"] : {}
  ) as Record<string, unknown>;
  const handle = typeof who["handle"] === "string" ? who["handle"].replace(/^@/, "") : "";
  const incoming = normaliseRecipientKnock(t["incoming"]);
  const outgoing = normaliseMyKnock(t["outgoing"]);
  // A thread with nothing in it is not a conversation.
  if (!incoming && !outgoing) return null;
  return {
    id,
    counterparty: {
      handle,
      display_name:
        typeof who["display_name"] === "string" && who["display_name"].trim()
          ? who["display_name"]
          : handle
            ? `@${handle}`
            : "Someone at the door",
      avatar: ownOriginAvatar(who["avatar"]),
    },
    connected: t["connected"] === true,
    last_activity_at:
      typeof t["last_activity_at"] === "string" && t["last_activity_at"]
        ? (t["last_activity_at"] as string)
        : null,
    incoming,
    outgoing,
  };
}

export async function fetchThreads(): Promise<ThreadsResult> {
  const r = await call(() => get("/api/v1/threads"));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  const raw = (r.body as Record<string, unknown>)["threads"];
  const threads: Thread[] = [];
  for (const entry of Array.isArray(raw) ? raw : []) {
    const thread = normaliseThread(entry);
    if (thread) threads.push(thread);
  }
  return { ok: true, threads };
}
